<template>
  <div class="w-full flex flex-col items-center">
    <div class="lg:w-1/3 lg:min-w-[420px]">
      <div class="rounded-md w-full content-center h-fit bg-vp-base-150">
        <h3 class="font-semibold px-4 pt-4">
          {{ $gettext('Contact info') }}
        </h3>
        <form
          id="payment-form"
          class="p-4"
          @submit.prevent="handleSubmit"
        >
          <div>
            <div class="relative mb-4">
              <input
                id="email"
                v-model="emailRef"
                type="email"
                :placeholder="$gettext('Email')"
                autocomplete="email"
                class="input-floating-label peer"
              >
              <label
                for="email"
                class="floating-label peer-placeholder-shown:top-1/2 peer-placeholder-shown:transform peer-placeholder-shown:-translate-y-1/2 peer-focus:top-3 peer-focus:text-sm peer-focus:text-vp-base-600 peer-focus:font-normal"
              >
                {{ $gettext('Email') }}
              </label>
            </div>
            <div class="relative mb-4">
              <input
                id="firstName"
                v-model="firstNameRef"
                type="text"
                :placeholder="$gettext('First name')"
                autocomplete="given-name"
                class="input-floating-label peer"
              >
              <label
                for="firstName"
                class="floating-label peer-placeholder-shown:top-1/2 peer-placeholder-shown:transform peer-placeholder-shown:-translate-y-1/2 peer-focus:top-3 peer-focus:text-sm peer-focus:text-vp-base-600 peer-focus:font-normal"
              >
                {{ $gettext('First name') }}
              </label>
            </div>
            <div class="relative mb-4">
              <input
                id="lastName"
                v-model="lastNameRef"
                type="text"
                :placeholder="$gettext('Last name')"
                autocomplete="family-name"
                class="input-floating-label peer"
              >
              <label
                for="lastName"
                class="floating-label peer-placeholder-shown:top-1/2 peer-placeholder-shown:transform peer-placeholder-shown:-translate-y-1/2 peer-focus:top-3 peer-focus:text-sm peer-focus:text-vp-base-600 peer-focus:font-normal"
              >
                {{ $gettext('Last name') }}
              </label>
            </div>
            <div class="relative">
              <VueTelInput
                v-model="phoneNumberRef"
                :dropdown-options="{ showDialCodeInList: true, showFlags: true }"
                :input-options="{ showDialCode: true }"
                mode="international"
                :valid-characters-only="true"
                default-country="US"
                :preferred-countries="['US', 'CA']"
                auto-format="true"
                style-classes="input-floating-label peer w-full"
              >
                <template #label>
                  <label
                    for="phoneNumber"
                    class="floating-label peer-placeholder-shown:top-1/2 peer-placeholder-shown:transform peer-placeholder-shown:-translate-y-1/2 peer-focus:top-3 peer-focus:text-sm peer-focus:text-vp-base-600 peer-focus:font-normal"
                  >
                    {{ $gettext('Mobile phone') }}
                  </label>
                </template>
              </VueTelInput>
              <span
                v-if="phoneErrorMessage"
                class="text-red-500 text-sm mt-1"
              >
                {{ phoneErrorMessage }}
              </span>
            </div>

            <div class="relative my-4">
              <input
                id="zipCode"
                v-model="postalCodeRef"
                type="text"
                :placeholder="$gettext('Zip code')"
                autocomplete="postal-code"
                class="input-floating-label peer"
              >
              <label
                for="zipCode"
                class="floating-label peer-placeholder-shown:top-1/2 peer-placeholder-shown:transform peer-placeholder-shown:-translate-y-1/2 peer-focus:top-3 peer-focus:text-sm peer-focus:text-vp-base-600 peer-focus:font-normal"
              >
                {{ $gettext('ZIP/Postal code') }}
              </label>
            </div>
          </div>
        </form>
      </div>
      <CheckoutCheckboxes
        :checkboxes="checkboxes"
        @update="v => (checkedCheckboxes = v)"
        @required-satisfied="v => (requiredSatisfied = v)"
      />
      <div class="w-full py-4">
        <VPButton
          type="submit"
          class="block w-full uppercase disabled:cursor-not-allowed"
          :disabled="isLoading || !isFormValid"
          :label="$gettext('Confirm')"
          variant="solid"
          size="xl"
          min-width="100%"
          @click="handleSubmit"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useCheckoutStore } from './stores/checkout';
import { useGettext } from 'vue3-gettext';
import VPButton from './components/VPButton.vue';
import { useRouter } from 'vue-router';
import CheckoutCheckboxes from './CheckoutCheckboxes.vue';
import { navigate } from 'vike/client/router';
import { useOrderDetails } from './useOrderDetails';
import { VueTelInput } from 'vue-tel-input';
import 'vue-tel-input/vue-tel-input.css';
import { useHive } from './integrations/useHive';
import { useToast } from 'vue-toastification';
import * as Sentry from '@sentry/vue';

const { $gettext } = useGettext();
const router = useRouter();

const isLoading = ref(false);
const checkoutStore = useCheckoutStore();
const checkedCheckboxes = ref<Record<string, boolean>>({});
const requiredSatisfied = ref<boolean>(false);
const checkboxes = computed(() => checkoutStore.getEventCheckoutConfig?.checkboxes || []);
const { orderDetails, setAddress, setCustomer, setAgreements } = useOrderDetails();
const { subscribeOrderCustomer } = useHive(computed(() => checkoutStore.getSelectedEvent?.config?.hiveBrandId));
const toast = useToast();

const eventId = computed(() => checkoutStore.getSelectedEvent?.id);

const emailRef = ref<string>('');
const firstNameRef = ref<string>('');
const lastNameRef = ref<string>('');
const phoneNumberRef = ref<string>('');
const postalCodeRef = ref<string>('');
const phoneErrorMessage = ref<string>('');

const isFormValid = computed(() => {
  return (
    firstNameRef.value.trim() !== '' &&
    lastNameRef.value.trim() !== '' &&
    emailRef.value.trim() !== '' &&
    requiredSatisfied.value
  );
});

const handleSubmit = async () => {
  if (isLoading.value) {
    return;
  }

  try {
    isLoading.value = true;
    orderDetails.value.eventId = eventId.value;
    setAddress({
      state: 'placeholder',
      street1: 'placeholder',
      street2: '',
      postalCode: postalCodeRef.value,
      city: 'placeholder',
      country: 'placeholder',
    });
    setCustomer({
      firstName: firstNameRef.value,
      lastName: lastNameRef.value,
      email: emailRef.value,
      phoneNumber: phoneNumberRef.value,
    });
    setAgreements(Object.keys(checkedCheckboxes.value).map(key => ({ key, value: checkedCheckboxes.value[key] })));

    const { orderSummary, errors } = await checkoutStore.saveOrder(orderDetails.value);
    if (errors && errors.length > 0) {
      errors.forEach(e => {
        toast.error(e.message);
      });
      return;
    }

    if (!orderSummary) {
      Sentry.captureException(new Error('saveOrder returned no order summary'));
      toast.error($gettext('An unexpected error has occurred.'));

      return;
    }

    const orderId = orderSummary.fingerprint;

    subscribeOrderCustomer(orderDetails.value.customerInformation);

    const finalize = router.resolve({ name: 'OrderFinalize', params: { eventId: eventId.value }, query: { shoppingCartToken: checkoutStore.shoppingCartToken, orderId } });
    await navigate(finalize.href);
  } finally {
    isLoading.value = false;
  }
};
</script>
